import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { X, Save, Trash2, Mail } from "lucide-react"
import { cn } from "@/lib/utils"
import { NotifyModal } from "./NotifyModal"
import { HeroInfoPopover } from "./HeroInfoPopover"
import { CreateContactModal } from "./CreateContactModal"
import { FortyKundeObjektPicker } from "./CalendarCreateModal"

interface CalOptions {
  categories: { id: number; name: string }[]
  partners: { id: number; name: string }[]
  monteure: { id: number; name: string }[]
  resources: { id: number; name: string }[]
  provider?: string
}

type FortyObjekt = { id: number; project_id: number | null; name: string; customer_id: number; kunde_name: string | null; street: string | null }

/**
 * Kalender-Event Edit-Modal — nutzt /api/v1/calendar/options + events PATCH/DELETE.
 * 8 Sektionen: Kategorie / Titel / Start / Ende / Mitarbeiter / Ressourcen / Projekt / Beschreibung.
 */
type Herkunft = {
  quelle: "telefonbot" | "portal" | "hero" | "unbekannt"
  wer: string | null
  wann: string | null
  call_id?: string | null
  geaendert: { wer: string; wann: string | null } | null
}

function fmtZeitpunkt(iso: string | null | undefined): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
}

/** Eine Zeile „Angelegt von … am …", nur intern sichtbar (#111). */
export function herkunftText(h: Herkunft | undefined): string | null {
  if (!h || !h.wer) return null
  const wann = fmtZeitpunkt(h.wann)
  let text = `Angelegt: ${h.wer}${wann ? ` · ${wann}` : ""}`
  if (h.quelle === "hero") text += " (HERO nennt den Nutzer nicht)"
  if (h.geaendert?.wer) {
    const w2 = fmtZeitpunkt(h.geaendert.wann)
    text += ` · zuletzt geändert: ${h.geaendert.wer}${w2 ? ` · ${w2}` : ""}`
  }
  return text
}

export function CalendarEditModal({
  open, onClose, tenant, event, onCopy,
}: {
  open: boolean
  onClose: () => void
  tenant: string
  event: {
    id: string
    title: string
    start: string
    end: string
    allDay?: boolean
    category_id?: number | null
    project_match_id?: number | null
    description?: string
    partner_ids?: number[]
  } | null
  onCopy?: (data: {
    title: string
    description: string
    categoryId: string
    partnerIds: number[]
    projectId: string
  }) => void
}) {
  const qc = useQueryClient()
  const [title, setTitle] = useState("")
  const [start, setStart] = useState("")
  const [end, setEnd] = useState("")
  // #118: ganztaegig — wer die Uhrzeit aendert, hebt es auf (sonst bleibt der Termin in der Ganztags-Zeile)
  const [allDay, setAllDay] = useState(false)
  const [categoryId, setCategoryId] = useState("")
  const [partnerIds, setPartnerIds] = useState<number[]>([])
  const [resourceIds, setResourceIds] = useState<number[]>([])
  const [description, setDescription] = useState("")
  const [projectId, setProjectId] = useState("")
  const [err, setErr] = useState<string | null>(null)
  const [notifyOpen, setNotifyOpen] = useState(false)
  const [notifyIsChange, setNotifyIsChange] = useState(true)
  // Was zuletzt in HERO stand bzw. gespeichert wurde — Vergleichsbasis fuer
  // „ungespeicherte Aenderung" vor dem Informieren (#142) und beim Schliessen.
  const [gespeichert, setGespeichert] = useState({
    start: "", end: "", title: "", description: "", categoryId: "", koepfe: "", projectId: "", allDay: false,
  })
  // fortytools: Kunde + Objekt statt HERO-Projekt-Verknüpfung
  const [fortyCustomer, setFortyCustomer] = useState<{ id: number; name: string } | null>(null)
  const [fortyObjektPid, setFortyObjektPid] = useState("")

  const opts = useQuery<CalOptions>({
    queryKey: ["cal-options", tenant],
    queryFn: async () => {
      const res = await fetch(`/api/v1/calendar/options?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: open && !!tenant,
    // Kategorien/Mitarbeiter aendern sich fast nie, der Call geht aber ueber
    // drei GraphQL-Roundtrips zu HERO (~1s). Ohne staleTime laedt jedes
    // Oeffnen eines Termins neu — und solange zeigt das Kategorie-Feld nichts.
    staleTime: 5 * 60_000,
  })
  const isForty = (opts.data as any)?.provider === "fortytools"

  // #111 (Marlene, 23.09.2026): wer hat den Termin wann angelegt? Telefonbot,
  // Portal-Nutzer oder „in HERO angelegt" (HERO nennt keinen Ersteller).
  const herkunft = useQuery<Herkunft>({
    queryKey: ["termin-herkunft", tenant, event?.id],
    enabled: open && !!event && !!tenant && /^\d+$/.test(String(event?.id ?? "")),
    queryFn: async () => {
      const res = await fetch(`/api/v1/calendar/termin-herkunft/${event!.id}?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    staleTime: 60_000,
  })
  const kategorien = opts.data?.categories || []
  // Die Kategorie des Termins steht sofort fest, die Liste kommt erst spaeter.
  // Fehlt sie in der Liste (noch nicht geladen, Call fehlgeschlagen, oder HERO
  // fuehrt die Kategorie nicht mehr), zeigt das Feld ohne diesen Ersatzeintrag
  // faelschlich "– Kategorie –" — also "keine Kategorie", obwohl der Termin eine hat.
  const kategorieFehlt = !!categoryId && !kategorien.some((c) => String(c.id) === categoryId)

  const fortyObjekte = useQuery<{ items: FortyObjekt[] }>({
    queryKey: ["objekte", tenant],
    enabled: open && isForty && !!tenant,
    queryFn: async () => {
      const res = await fetch(`/api/v1/projects/objekte?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })
  const kundenObjekte = (fortyObjekte.data?.items || []).filter((o) => !fortyCustomer || o.customer_id === fortyCustomer.id)

  useEffect(() => {
    if (open && event) {
      setTitle(event.title)
      setStart(toLocalInput(event.start))
      setEnd(toLocalInput(event.end))
      setAllDay(!!event.allDay)
      setCategoryId(event.category_id ? String(event.category_id) : "")
      // Roh uebernehmen und als Zahlen fuehren — die Aufteilung in Mitarbeiter
      // und Ressourcen passiert im Effekt darunter, sobald die Listen da sind.
      setPartnerIds((event.partner_ids || []).map(Number))
      setResourceIds([])
      koepfeGetrennt.current = false
      setDescription(event.description || "")
      setGespeichert({
        start: toLocalInput(event.start), end: toLocalInput(event.end),
        title: event.title, description: event.description || "",
        categoryId: event.category_id ? String(event.category_id) : "",
        koepfe: koepfeSchluessel((event.partner_ids || []).map(Number)),
        projectId: event.project_match_id ? String(event.project_match_id) : "",
        allDay: !!event.allDay,
      })
      setProjectId(event.project_match_id ? String(event.project_match_id) : "")
      setFortyCustomer(null)
      setFortyObjektPid("")
      setErr(null)
    }
  }, [open, event])

  // 🔴 Mitarbeiter und Ressourcen wieder auseinandersortieren.
  //
  // HERO fuehrt beides getrennt (`partner_ids` / `resource_ids`), unser Cache
  // legt sie aber in EINER Spalte zusammen. Das Formular bekam bisher alles als
  // "Mitarbeiter" und die Ressourcen fest auf []. Zwei Folgen, beide beim
  // Uebernehmen sichtbar (10.08.2026, Termin „Wasserschaden reparieren Probepopp"):
  //   1. Kenan Musanovic ist in HERO eine RESSOURCE. Sein Haken fehlte in
  //      beiden Listen — in der Mitarbeiterliste zusaetzlich deshalb, weil er
  //      von dort ueber `monteure.hero_partner_id` kommt, einer TEXT-Spalte:
  //      [120923, 23407].includes("23407") ist false. Darum ueberall Number().
  //   2. `resource_ids: []` loeschte beim Speichern jede Ressourcen-Zuordnung.
  // Die Trennung braucht die geladenen Options-Listen, laeuft also erst wenn
  // die da sind — und nur einmal je geoeffnetem Termin, sonst wuerde ein
  // spaeteres Neuladen der Listen die Haken des Nutzers zuruecksetzen.
  const koepfeGetrennt = useRef(false)
  useEffect(() => {
    if (!open || !event || koepfeGetrennt.current) return
    const resIds = (opts.data?.resources || []).map((r) => Number(r.id))
    if (!resIds.length && !opts.data) return
    const koepfe = (event.partner_ids || []).map(Number)
    setResourceIds(koepfe.filter((id) => resIds.includes(id)))
    setPartnerIds(koepfe.filter((id) => !resIds.includes(id)))
    koepfeGetrennt.current = true
  }, [open, event, opts.data])

  // fortytools: aktuelles Objekt (+ Kunde) aus project_match_id vorbefüllen,
  // sobald die Objekt-Liste geladen ist. project_match_id eines Portal-Termins
  // = project_id der Facility (Objekt).
  useEffect(() => {
    if (!open || !isForty || !event?.project_match_id) return
    const o = (fortyObjekte.data?.items || []).find((x) => x.project_id === event.project_match_id)
    if (o) {
      setFortyObjektPid(String(o.project_id))
      setFortyCustomer({ id: o.customer_id, name: o.kunde_name || `Kunde #${o.customer_id}` })
    }
  }, [open, isForty, event?.project_match_id, fortyObjekte.data])

  const save = useMutation({
    mutationFn: async (_vars?: { offenLassen?: boolean }) => {
      const res = await fetch("/api/v1/calendar/events", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          tenant,
          event_id: event?.id,
          start: new Date(start).toISOString(),
          end: new Date(end).toISOString(),
          all_day: allDay,
          title,
          description,
          category_id: categoryId ? parseInt(categoryId) : null,
          // 🔴 ALLE Koepfe zusammen in `partner_ids` — die Trennung oben ist
          // reine Anzeige. Der Update-Pfad im Backend teilt selbst auf
          // (`_split_partner_resource_heads` gegen `monteure.hero_kind`) und
          // liest `resource_ids` beim Bearbeiten gar nicht. Wer hier nur die
          // Mitarbeiter schickt, loescht damit die Ressourcen aus dem Termin:
          // das Backend sieht sie nicht mehr und setzt resource_ids leer.
          // (Beinahe-Fehler am 10.08.2026 — genau andersherum als gedacht.)
          partner_ids: [...partnerIds, ...resourceIds],
          resource_ids: resourceIds,
          // fortytools: Objekt-Verknüpfung via forty_project_id (Backend mappt
          // es auf project_match_id des lokalen Termins). HERO: project_match_id.
          project_match_id: isForty ? undefined : (projectId ? parseInt(projectId) : null),
          forty_project_id: isForty && fortyObjektPid ? parseInt(fortyObjektPid) : undefined,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
      return json
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["calendar"] })
      if (vars?.offenLassen) {
        setGespeichert({
          start, end, title, description, categoryId,
          koepfe: koepfeSchluessel([...partnerIds, ...resourceIds]), projectId, allDay,
        })
        return
      }
      onClose()
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  // 🔴 #142 (29.09.2026, Termin Testa): Uhrzeit im Dialog auf 13:00 geaendert,
  // „Über Änderung informieren" geklickt, Mail ging raus — „Übernehmen" nie.
  // Die Kundin hatte eine Bestaetigung fuer 13:00, in HERO stand weiter
  // 11:30. Die Mail nimmt Zeit und Titel aus dem FORMULAR, nicht aus HERO.
  // Darum: ungespeicherte Aenderungen erst speichern, dann informieren.
  // Scheitert das Speichern, geht keine Mail auf.
  const ungespeichert = !!event && (
    start !== gespeichert.start || end !== gespeichert.end
    || title !== gespeichert.title || description !== gespeichert.description
  )
  // Alles, was „Übernehmen" in HERO schreiben wuerde. Die Mail (#142) haengt
  // nur an Zeit/Titel/Beschreibung, das Verwerfen beim Schliessen an allem.
  const irgendwasUngespeichert = ungespeichert || (!!event && !isForty && (
    categoryId !== gespeichert.categoryId
    || koepfeSchluessel([...partnerIds, ...resourceIds]) !== gespeichert.koepfe
    || projectId !== gespeichert.projectId
    || allDay !== gespeichert.allDay
  ))

  // Neue Uhrzeit (nicht nur neues Datum) → nicht mehr ganztaegig (#118)
  const zeitAendern = (alt: string, neu: string, setzen: (v: string) => void) => {
    if (allDay && neu.slice(11) !== alt.slice(11)) setAllDay(false)
    setzen(neu)
  }

  // Paul, 30.09.2026: „die Warnung, dass ungespeicherte Änderungen verloren
  // gehen". Bisher verwarf Abbrechen, X oder ein Klick daneben jede Eingabe
  // wortlos — beim Termin Testa blieb so die 13:00 nur in der Kunden-Mail.
  const schliessen = () => {
    if (irgendwasUngespeichert && !save.isPending
        && !confirm("Ungespeicherte Änderungen am Termin gehen verloren.\n\nOhne Speichern schließen?")) {
      return
    }
    onClose()
  }

  const informieren = async (isChange: boolean) => {
    if (ungespeichert) {
      setErr(null)
      try {
        await save.mutateAsync({ offenLassen: true })
      } catch {
        return
      }
    }
    setNotifyIsChange(isChange)
    setNotifyOpen(true)
  }

  const del = useMutation({
    mutationFn: async (opts: { delete_series?: boolean }) => {
      const res = await fetch("/api/v1/calendar/events", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tenant, event_id: event?.id, delete_series: !!opts?.delete_series }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar"] })
      onClose()
    },
  })

  const [seriesPromptOpen, setSeriesPromptOpen] = useState(false)
  const hasSeries = !!(event as any)?.recurrence_series_id

  if (!open || !event) return null

  return (
    <div
      className="pp-overlay"
      data-portal-modal
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) schliessen() }}
    >
      <form
        onSubmit={(ev) => { ev.preventDefault(); setErr(null); save.mutate({}) }}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          width: "100%", maxWidth: 640, maxHeight: "85vh",
          overflow: "hidden", display: "flex", flexDirection: "column",
        }}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-[15px] font-semibold">Termin bearbeiten</h2>
          <button type="button" onClick={schliessen} aria-label="Schließen"><X className="w-4 h-4" /></button>
        </div>

        <div className="overflow-y-auto p-5 space-y-3">
          {err && <div className="pp-alert pp-alert-err">{err}</div>}
          {herkunftText(herkunft.data) && (
            <div data-testid="termin-herkunft" className="text-[12px] text-muted-foreground -mt-1">
              {herkunftText(herkunft.data)}
            </div>
          )}

          <div>
            <label className="pp-form-label" htmlFor="cal-edit-category">Kategorie</label>
            <select
              id="cal-edit-category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={opts.isPending}
            >
              <option value="">– Kategorie –</option>
              {kategorieFehlt && <option value={categoryId}>Kategorie #{categoryId}</option>}
              {kategorien.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {opts.isPending && (
              <div style={{ marginTop: 3, fontSize: 12, color: "var(--text-muted)" }}>Kategorien werden geladen…</div>
            )}
            {opts.isError && (
              <div style={{ marginTop: 3, fontSize: 12, color: "#b91c1c" }}>
                Kategorie-Liste nicht ladbar — die Kategorie des Termins bleibt beim Speichern unveraendert.
              </div>
            )}
          </div>

          <div>
            <label className="pp-form-label">Titel</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="pp-form-label" htmlFor="termin-start">Start</label>
              <input id="termin-start" type="datetime-local" value={start}
                     onChange={(e) => zeitAendern(start, e.target.value, setStart)} required />
            </div>
            <div>
              <label className="pp-form-label" htmlFor="termin-ende">Ende</label>
              <input id="termin-ende" type="datetime-local" value={end}
                     onChange={(e) => zeitAendern(end, e.target.value, setEnd)} required />
            </div>
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
            Ganztägig
          </label>

          <div>
            <label className="pp-form-label">Mitarbeiter</label>
            <div style={{ maxHeight: 120, overflowY: "auto", border: "1px solid var(--border-color)", borderRadius: 6, padding: 8 }}>
              {[...(opts.data?.partners || []), ...(opts.data?.monteure || [])]
                // IDs kommen aus zwei Quellen: HERO liefert Zahlen, unsere
                // `monteure`-Tabelle Strings (Spalte ist TEXT). Ohne Number()
                // greift weder das Dedupe noch der Haken.
                .map((p) => ({ ...p, id: Number(p.id) }))
                .filter((p, i, arr) => arr.findIndex((x) => x.id === p.id || x.name.trim() === p.name.trim()) === i)
                // Wer in HERO eine Ressource ist, gehoert in die Liste
                // darunter — sonst steht er doppelt da und wird in beiden
                // Feldern mitgeschickt.
                .filter((p) => !(opts.data?.resources || []).some((r) => Number(r.id) === p.id))
                .map((p) => (
                  <label key={p.id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginBottom: 2 }}>
                    <input
                      type="checkbox"
                      checked={partnerIds.includes(p.id)}
                      onChange={(e) => setPartnerIds(
                        e.target.checked
                          ? [...partnerIds, p.id]
                          : partnerIds.filter((x) => x !== p.id),
                      )}
                    />
                    {p.name}
                  </label>
                ))}
            </div>
          </div>

          {(opts.data?.resources?.length ?? 0) > 0 && (
            <div>
              <label className="pp-form-label">Ressourcen</label>
              <div style={{ maxHeight: 100, overflowY: "auto", border: "1px solid var(--border-color)", borderRadius: 6, padding: 8 }}>
                {opts.data!.resources.map((r) => ({ ...r, id: Number(r.id) })).map((r) => (
                  <label key={r.id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginBottom: 2 }}>
                    <input
                      type="checkbox"
                      checked={resourceIds.includes(r.id)}
                      onChange={(e) => setResourceIds(
                        e.target.checked
                          ? [...resourceIds, r.id]
                          : resourceIds.filter((x) => x !== r.id),
                      )}
                    />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {isForty ? (
            <div>
              <label className="pp-form-label">Kunde &amp; Objekt</label>
              <FortyKundeObjektPicker
                tenant={tenant}
                objekte={kundenObjekte}
                customer={fortyCustomer}
                objektPid={fortyObjektPid}
                onCustomer={(c) => { setFortyCustomer(c); setFortyObjektPid("") }}
                onObjekt={setFortyObjektPid}
                onTitleSuggest={() => { /* Titel im Edit nicht überschreiben */ }}
                defaultTitleWasUsed={false}
              />
            </div>
          ) : (
            <ProjectLinker
              tenant={tenant}
              projectId={projectId}
              onChange={setProjectId}
              onNotifyInitial={() => { void informieren(false) }}
              onNotifyChange={() => { void informieren(true) }}
              speichertVorher={ungespeichert}
            />
          )}

          <div>
            <label className="pp-form-label">Beschreibung</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </div>
        </div>

        <div className="px-5 py-3 border-t flex justify-between gap-2">
          <button
            type="button"
            onClick={() => {
              if (hasSeries) {
                setSeriesPromptOpen(true)
              } else if (confirm("Termin wirklich löschen?")) {
                del.mutate({ delete_series: false })
              }
            }}
            disabled={del.isPending}
            className="pp-btn pp-btn-sm pp-btn-danger"
          >
            <Trash2 className="w-3 h-3" /> Löschen
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={schliessen} className="pp-btn pp-btn-sm">Abbrechen</button>
            {onCopy && (
              <button
                type="button"
                onClick={() => onCopy({ title, description, categoryId, partnerIds, projectId })}
                className="pp-btn pp-btn-sm"
              >
                Kopieren
              </button>
            )}
            <button
              type="submit"
              disabled={save.isPending}
              className={cn("pp-btn pp-btn-primary pp-btn-sm", save.isPending && "opacity-50")}
            >
              <Save className="w-3 h-3" /> {save.isPending ? "Übernimmt..." : "Übernehmen"}
            </button>
          </div>
        </div>
      </form>
      {projectId && (
        <NotifyModal
          open={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          tenant={tenant}
          projectId={projectId}
          title={title}
          start={start}
          end={end}
          description={description}
          isChange={notifyIsChange}
        />
      )}
      {seriesPromptOpen && (
        <div
          className="pp-overlay"
          style={{
            position: "fixed", inset: 0, zIndex: 1100,
            background: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}
          onMouseDown={(ev) => { if (ev.target === ev.currentTarget) setSeriesPromptOpen(false) }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 10, padding: "18px 22px",
              width: 420, maxWidth: "95vw", boxShadow: "0 12px 36px rgba(0,0,0,0.25)",
            }}
          >
            <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 600 }}>Wiederkehrender Termin löschen</h3>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#475569" }}>
              Dieser Termin gehört zu einer Serie. Was soll gelöscht werden?
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                type="button"
                onClick={() => { setSeriesPromptOpen(false); del.mutate({ delete_series: false }) }}
                style={{
                  padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 6,
                  background: "#fff", cursor: "pointer", textAlign: "left", fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600 }}>Nur diesen Termin</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>Andere Folge-Termine bleiben erhalten.</div>
              </button>
              <button
                type="button"
                onClick={() => { setSeriesPromptOpen(false); del.mutate({ delete_series: true }) }}
                style={{
                  padding: "10px 14px", border: "1px solid #fecaca", borderRadius: 6,
                  background: "#fef2f2", cursor: "pointer", textAlign: "left", fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600, color: "#b91c1c" }}>Gesamte Serie</div>
                <div style={{ fontSize: 12, color: "#991b1b" }}>Alle Termine dieser Serie werden gelöscht.</div>
              </button>
              <button
                type="button"
                onClick={() => setSeriesPromptOpen(false)}
                style={{
                  padding: "8px 14px", border: "none", borderRadius: 6,
                  background: "transparent", cursor: "pointer", fontSize: 12.5, color: "#64748b",
                  marginTop: 4,
                }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** Mitarbeiter + Ressourcen als vergleichbarer Schluessel — Reihenfolge egal. */
function koepfeSchluessel(ids: number[]): string {
  return [...ids].map(Number).sort((a, b) => a - b).join(",")
}

function ProjectLinker({
  tenant, projectId, onChange, onNotifyInitial, onNotifyChange, speichertVorher = false,
}: {
  tenant: string
  projectId: string
  onChange: (id: string) => void
  onNotifyInitial: () => void
  onNotifyChange: () => void
  /** Ungespeicherte Aenderung im Dialog — der Knopf speichert erst in HERO (#142). */
  speichertVorher?: boolean
}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<Array<{ id: number; name: string; status?: string; contact_name?: string; contact_email?: string }>>([])
  const [searching, setSearching] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createUseExisting, setCreateUseExisting] = useState(false)

  // Projekt-Details (inkl. Adresse) nachladen wenn verknuepft
  const projectInfo = useQuery({
    queryKey: ["hero-lookup-project", tenant, projectId],
    queryFn: async () => {
      if (!projectId) return null
      const r = await fetch(
        `/api/v1/hero/lookup?tenant=${encodeURIComponent(tenant)}&project_id=${encodeURIComponent(projectId)}`,
        { credentials: "include" },
      )
      if (!r.ok) return null
      return r.json()
    },
    enabled: !!projectId && !!tenant,
    staleTime: 60_000,
  })
  // HERO hat ZWEI Adressen: contact.address (Stammadresse des Kunden) und
  // project_match.address (Baustelle, kann abweichen — z.B. Sanitec-Projekt
  // SAN-690 hat Kunden-Adresse "Roonstraße" aber Baustelle "Johann-Georg-
  // Schlosser-Straße"). Im Kalender-Kontext interessiert uns die Baustelle.
  // Fallback auf Kontakt-Adresse wenn project_match.address leer ist.
  const addr = (() => {
    const projAddr = projectInfo.data?.projects?.[0]?.address
    if (projAddr && (projAddr.street || projAddr.city || projAddr.zipcode)) {
      const parts = [projAddr.street, [projAddr.zipcode, projAddr.city].filter(Boolean).join(" ")].filter(Boolean)
      return parts.join(", ")
    }
    const c = projectInfo.data?.contact
    if (!c) return ""
    const parts = [c.street, [c.zipcode, c.city].filter(Boolean).join(" ")].filter(Boolean)
    return parts.join(", ")
  })()
  // 🔴 HERO-Bezeichnung statt interner ID: „LÜA-3470 — Klimaanlage Einsatz
  // Karlsruhe", nicht „Projekt #11475113". `name` kommt aus hero_lookup und
  // enthaelt bereits Nummer + Titel.
  const projName = projectInfo.data?.projects?.[0]?.name || projectInfo.data?.projects?.[0]?.title || ""

  const doSearch = async (q: string) => {
    if (q.length < 2) { setResults([]); return }
    setSearching(true)
    try {
      const url = `/api/v1/projects/search?tenant=${encodeURIComponent(tenant)}&q=${encodeURIComponent(q)}`
      const r = await fetch(url, { credentials: "include" })
      const d = await r.json()
      setResults(d.items || [])
    } catch { setResults([]) }
    finally { setSearching(false) }
  }

  return (
    <div>
      <label className="pp-form-label">Projekt-Verknüpfung</label>
      {projectId ? (
        <div style={{ padding: "8px 12px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <HeroInfoPopover tenantSlug={tenant} projectId={projectId}>
              <span style={{ fontSize: 12.5, cursor: "help", color: "var(--accent-color)", fontWeight: 600 }}>
                {projName || `Vorgang wird geladen …`}
              </span>
            </HeroInfoPopover>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={onNotifyInitial}
              className="pp-btn pp-btn-sm"
              title={speichertVorher
                ? "Speichert die Änderung zuerst in HERO, dann Bestätigungs-Mail an den Kunden"
                : "KI-generierte Bestätigungs-Mail (neuer Termin) an Kunden senden"}
            >
              <Mail className="w-3 h-3" /> Über Termin informieren
            </button>
            <button
              type="button"
              onClick={onNotifyChange}
              className="pp-btn pp-btn-sm"
              title={speichertVorher
                ? "Speichert die Änderung zuerst in HERO, dann Änderungs-Mail an den Kunden"
                : "KI-generierte Änderungs-Mail (Termin verschoben) an Kunden senden"}
            >
              <Mail className="w-3 h-3" /> {speichertVorher ? "Speichern + informieren" : "Über Änderung informieren"}
            </button>
            <button
              type="button"
              onClick={() => onChange("")}
              className="pp-btn pp-btn-sm"
              title="Verknüpfung entfernen"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          {addr && (
            <div style={{ marginTop: 4, fontSize: 12, color: "#475569" }}>
              📍 {addr}
            </div>
          )}
          {projectInfo.isLoading && (
            <div style={{ marginTop: 4, fontSize: 12, color: "#94a3b8" }}>Lade Adresse…</div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); doSearch(e.target.value) }}
              placeholder="Projekt suchen (Name, Nummer, Kontakt...)"
              style={{ flex: "1 1 200px" }}
            />
            <button
              type="button"
              onClick={() => { setCreateUseExisting(false); setCreateOpen(true) }}
              style={{
                background: "var(--accent-color, #3C3489)", color: "#fff", border: "none",
                padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              title="Neues Projekt + neuer Kontakt in HERO anlegen"
            >+ Neu</button>
            <button
              type="button"
              onClick={() => { setCreateUseExisting(true); setCreateOpen(true) }}
              style={{
                background: "#fff", color: "var(--accent-color, #3C3489)",
                border: "1px solid var(--accent-color, #3C3489)",
                padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              title="Neues Projekt für einen bereits existierenden HERO-Kontakt anlegen"
            >+ Für bestehenden Kontakt</button>
          </div>
          {searching && <div style={{ fontSize: 12, color: "var(--text-muted)", padding: 6 }}>Suche...</div>}
          {results.length > 0 && (
            <div style={{
              marginTop: 4, maxHeight: 160, overflowY: "auto",
              border: "1px solid var(--border-color)", borderRadius: 6,
            }}>
              {results.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { onChange(String(p.id)); setQuery(""); setResults([]) }}
                  style={{
                    width: "100%", textAlign: "left", padding: "8px 10px",
                    borderBottom: "1px solid var(--border-color)",
                    background: "none", border: "none", cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {[p.contact_name, p.contact_email, p.status || "kein Status", `ID ${p.id}`].filter(Boolean).join(" · ")}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <CreateContactModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        mode="calendar"
        itemId={0}
        tenantSlug={tenant}
        initialUseExistingContact={createUseExisting}
        onSuccess={(result) => {
          if (result?.project_id) onChange(String(result.project_id))
          setCreateOpen(false)
        }}
      />
    </div>
  )
}

function toLocalInput(iso: string): string {
  try {
    const d = new Date(iso)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    const h = String(d.getHours()).padStart(2, "0")
    const min = String(d.getMinutes()).padStart(2, "0")
    return `${y}-${m}-${day}T${h}:${min}`
  } catch {
    return ""
  }
}
